/**
 * PagePubliqueEglise — vitrine publique d'une église (sans login).
 * URL : /eglise/:slug
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi } from "../services/api";
import { colors, fonts } from "../styles/tokens";

const FALLBACK_GOLD = "#E4A72C";

export default function PagePubliqueEglise() {
  const { slug } = useParams();
  const [eglise, setEglise] = useState(null);
  const [evenements, setEvenements] = useState([]);
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    publicApi
      .eglise(slug)
      .then(({ data }) => {
        if (cancelled) return;
        setEglise(data);
        if (data?.show_events) {
          publicApi
            .evenements(slug)
            .then((r) => { if (!cancelled) setEvenements(r.data || []); })
            .catch(() => { if (!cancelled) setEvenements([]); });
        }
        publicApi
          .stream(slug)
          .then((r) => { if (!cancelled) setStream(r.data || null); })
          .catch(() => { if (!cancelled) setStream(null); });
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        setError(status === 404 ? "Cette page n'existe pas ou n'est pas active." : "Erreur de chargement.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [slug]);

  // SEO : titre + meta tags dynamiques
  useEffect(() => {
    if (!eglise) return;
    const title = `${eglise.nom} — Mon Ministère`;
    document.title = title;

    const desc = `Église ${eglise.nom} à ${eglise.ville}. Cultes, événements, dons en ligne. Rejoignez notre communauté.`;
    setMeta("description", desc);
    setMeta("og:title", eglise.nom, "property");
    setMeta("og:description", eglise.description || desc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", window.location.href, "property");
  }, [eglise]);

  if (loading) {
    return (
      <CenteredScreen>
        <div style={{ color: colors.textMuted, fontFamily: fonts.body }}>Chargement…</div>
      </CenteredScreen>
    );
  }

  if (error || !eglise) {
    return (
      <CenteredScreen>
        <div style={{ textAlign: "center", color: colors.text, fontFamily: fonts.body }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✝</div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: 28, marginBottom: 8 }}>Page introuvable</h1>
          <p style={{ color: colors.textMuted, marginBottom: 20 }}>{error || "Cette page n'existe pas."}</p>
          <Link to="/" style={{ color: colors.gold, fontWeight: 600 }}>← Retour à l'accueil</Link>
        </div>
      </CenteredScreen>
    );
  }

  const accent = eglise.couleur_principale || FALLBACK_GOLD;

  return (
    <div style={{
      background: colors.bg,
      color: colors.text,
      minHeight: "100vh",
      fontFamily: fonts.body,
    }}>
      <Header eglise={eglise} accent={accent} />
      <main style={{ maxWidth: 880, margin: "0 auto", padding: "0 clamp(16px, 4vw, 24px) 80px", display: "flex", flexDirection: "column", gap: 32 }}>
        {stream?.actif && stream.embed_url && (
          <LiveStream stream={stream} eglise={eglise} accent={accent} />
        )}
        {!stream?.actif && stream?.prochain_date && (
          <ProchainStream stream={stream} eglise={eglise} accent={accent} />
        )}
        {Array.isArray(eglise.horaires_culte) && eglise.horaires_culte.length > 0 && (
          <Horaires horaires={eglise.horaires_culte} accent={accent} />
        )}
        {eglise.show_events && <Evenements evenements={evenements} accent={accent} />}
        {Array.isArray(stream?.replays) && stream.replays.length > 0 && (
          <Replays replays={stream.replays} accent={accent} />
        )}
        {eglise.show_don && <DonForm slug={slug} devise={eglise.devise} accent={accent} />}
        {eglise.show_contact && <ContactForm slug={slug} accent={accent} />}
      </main>
      <PublicFooter />
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────

function Header({ eglise, accent }) {
  return (
    <header style={{
      padding: "60px clamp(16px, 4vw, 24px) 40px",
      background: `linear-gradient(160deg, ${accent}25 0%, ${colors.bg} 70%)`,
      borderBottom: `1px solid ${colors.border}`,
    }}>
      <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 44, color: accent, marginBottom: 12 }}>✝</div>
        <h1 style={{
          fontFamily: fonts.heading,
          fontSize: "clamp(32px, 6vw, 48px)",
          fontWeight: 700,
          marginBottom: 8,
          letterSpacing: "-0.01em",
        }}>
          {eglise.titre || eglise.nom}
        </h1>
        {eglise.pasteur_principal && (
          <p style={{ color: colors.textMuted, fontSize: 16, marginBottom: 4 }}>
            Pasteur {eglise.pasteur_principal}
          </p>
        )}
        <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 24 }}>
          📍 {eglise.ville}{eglise.pays ? `, ${eglise.pays}` : ""}
        </p>
        {eglise.description && (
          <p style={{ color: colors.text, fontSize: 16, maxWidth: 600, margin: "0 auto 24px", lineHeight: 1.6 }}>
            {eglise.description}
          </p>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          {eglise.show_don && (
            <a href="#don" style={ctaButton(accent)}>💰 Faire un don</a>
          )}
          {eglise.show_contact && (
            <a href="#contact" style={ctaButtonGhost(accent)}>📞 Contact</a>
          )}
        </div>
      </div>
    </header>
  );
}

function Horaires({ horaires, accent }) {
  return (
    <Section title="🕐 Nos cultes" accent={accent}>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {(horaires || []).map((h, i) => (
          <li key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: colors.card, borderRadius: 10, border: `1px solid ${colors.border}` }}>
            <span style={{ fontWeight: 600 }}>{h.jour}</span>
            <span style={{ color: accent, fontWeight: 600 }}>{h.heure}{h.type ? ` · ${h.type}` : ""}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Evenements({ evenements, accent }) {
  if (!evenements || evenements.length === 0) {
    return (
      <Section title="📅 Événements à venir" accent={accent}>
        <p style={{ color: colors.textMuted }}>Aucun événement programmé pour l'instant.</p>
      </Section>
    );
  }
  return (
    <Section title="📅 Événements à venir" accent={accent}>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {evenements.map((e) => (
          <li key={e.id} style={{ padding: "12px 14px", background: colors.card, borderRadius: 10, border: `1px solid ${colors.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600 }}>{e.titre}</div>
              <div style={{ color: accent, fontSize: 14 }}>
                {new Date(e.date_debut).toLocaleString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            {e.lieu && <div style={{ color: colors.textMuted, fontSize: 13, marginTop: 4 }}>📍 {e.lieu}</div>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function DonForm({ slug, devise, accent }) {
  const [montant, setMontant] = useState("");
  const [typeDon, setTypeDon] = useState("offrande");
  const [nom, setNom] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const value = parseFloat(montant);
    if (!value || value <= 0) {
      setError("Montant invalide");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.don(slug, { montant: value, type_don: typeDon, nom: nom || null });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Section id="don" title="💰 Soutenir l'église" accent={accent}>
        <div style={{ background: colors.card, border: `1px solid ${accent}`, borderRadius: 12, padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🙏</div>
          <p style={{ color: colors.text, fontSize: 16 }}>Merci pour votre don, que Dieu vous bénisse !</p>
        </div>
      </Section>
    );
  }

  return (
    <Section id="don" title="💰 Soutenir l'église" accent={accent}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label={`Montant (${devise || "EUR"})`}>
          <input
            type="number" min="1" step="0.01"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            required
            style={inputStyle}
          />
        </Field>
        <Field label="Type">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { v: "dime", l: "Dîme" },
              { v: "offrande", l: "Offrande" },
              { v: "projet", l: "Don spécial" },
            ].map((opt) => (
              <label key={opt.v} style={{
                ...pillStyle,
                background: typeDon === opt.v ? accent : colors.card,
                color: typeDon === opt.v ? "#050709" : colors.text,
                borderColor: typeDon === opt.v ? accent : colors.border,
              }}>
                <input type="radio" name="type_don" value={opt.v} checked={typeDon === opt.v} onChange={() => setTypeDon(opt.v)} style={{ display: "none" }} />
                {opt.l}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Nom (optionnel)">
          <input
            type="text"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            placeholder="Anonyme par défaut"
            style={inputStyle}
          />
        </Field>
        {error && <div style={{ color: colors.rose, fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={submitting} style={primaryButton(accent, submitting)}>
          {submitting ? "Envoi…" : "Enregistrer mon don"}
        </button>
        <p style={{ fontSize: 12, color: colors.textMuted, textAlign: "center", marginTop: 4 }}>
          🔒 Don sécurisé · Reçu disponible
        </p>
      </form>
    </Section>
  );
}

function ContactForm({ slug, accent }) {
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await publicApi.contact(slug, { nom, email, message });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Section id="contact" title="📞 Nous contacter" accent={accent}>
        <div style={{ background: colors.card, border: `1px solid ${accent}`, borderRadius: 12, padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✉️</div>
          <p style={{ color: colors.text }}>Votre message a bien été envoyé. Nous vous répondrons rapidement 🙏</p>
        </div>
      </Section>
    );
  }

  return (
    <Section id="contact" title="📞 Nous contacter" accent={accent}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Votre nom">
          <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} required style={inputStyle} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
        </Field>
        <Field label="Message">
          <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
        {error && <div style={{ color: colors.rose, fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={submitting} style={primaryButton(accent, submitting)}>
          {submitting ? "Envoi…" : "Envoyer le message"}
        </button>
      </form>
    </Section>
  );
}

// ─── Streaming ────────────────────────────────────────────────────────

function LiveStream({ stream, eglise, accent }) {
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareWa = `https://wa.me/?text=${encodeURIComponent(
    `🔴 LIVE — ${stream.titre || "Culte en direct"} de ${eglise.nom} : ${shareUrl}`
  )}`;

  return (
    <section style={{ paddingTop: 8 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{
          background: "#E05B6A",
          color: "white",
          padding: "4px 12px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.05em",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          animation: "pulse-red 1.5s infinite",
        }}>
          ● EN DIRECT MAINTENANT
        </span>
        {stream.titre && (
          <span style={{ color: colors.text, fontWeight: 600, fontSize: 16 }}>
            {stream.titre} · {eglise.nom}
          </span>
        )}
      </div>

      <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
        <iframe
          src={stream.embed_url}
          title="Culte en direct"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
        />
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
        {stream.url && (
          <a href={stream.url} target="_blank" rel="noreferrer" style={ctaButtonGhost(accent)}>
            👁 Plein écran
          </a>
        )}
        <a href={shareWa} target="_blank" rel="noreferrer" style={ctaButtonGhost(accent)}>
          📤 Partager ce live
        </a>
      </div>
    </section>
  );
}

function ProchainStream({ stream, eglise, accent }) {
  const target = new Date(stream.prochain_date);
  const [countdown, setCountdown] = useState(() => formatCountdown(target));

  useEffect(() => {
    const timer = setInterval(() => setCountdown(formatCountdown(target)), 60000);
    return () => clearInterval(timer);
  }, [stream.prochain_date]);

  const dateStr = target.toLocaleString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
  });

  const rappelUrl = `https://wa.me/33695215989?text=${encodeURIComponent(
    `Bonjour 🙏 Je souhaite être rappelé(e) pour le culte en direct de ${eglise.nom} le ${dateStr}.`
  )}`;

  return (
    <section style={{
      background: colors.card,
      border: `1px solid ${accent}`,
      borderRadius: 12,
      padding: "24px 20px",
    }}>
      <div style={{ fontSize: 14, color: accent, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>
        📺 PROCHAIN CULTE EN DIRECT
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, fontFamily: fonts.heading, marginBottom: 4 }}>
        {dateStr}
      </div>
      {stream.titre && (
        <div style={{ color: colors.textMuted, fontSize: 14, marginBottom: 16, fontStyle: "italic" }}>
          « {stream.titre} »
        </div>
      )}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: colors.textMuted }}>⏰ Dans</span>
        <span style={{
          fontFamily: "monospace",
          fontSize: 22,
          fontWeight: 700,
          color: accent,
          background: colors.bg,
          padding: "6px 14px",
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
        }}>
          {countdown}
        </span>
      </div>
      <a href={rappelUrl} target="_blank" rel="noreferrer" style={ctaButton(accent)}>
        🔔 Me rappeler
      </a>
    </section>
  );
}

function Replays({ replays, accent }) {
  return (
    <Section title="🎬 Replays des cultes" accent={accent}>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {replays.map((r, i) => {
          const ytId = extractYouTubeId(r.url);
          const thumb = ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : null;
          return (
            <li key={i}>
              <a href={r.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "inherit", display: "block" }}>
                <div style={{
                  position: "relative",
                  paddingTop: "56.25%",
                  borderRadius: 10,
                  overflow: "hidden",
                  background: thumb ? `center/cover no-repeat url(${thumb})` : colors.card,
                  border: `1px solid ${colors.border}`,
                  marginBottom: 8,
                }}>
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(0,0,0,0.35)",
                    fontSize: 32,
                    color: "white",
                  }}>▶</div>
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, color: colors.text, lineHeight: 1.3 }}>{r.titre}</div>
                {r.date && (
                  <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>
                    {new Date(r.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|live\/|embed\/|shorts\/))([^&\n?]+)/);
  return m ? m[1] : null;
}

function formatCountdown(target) {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return "C'est maintenant !";
  const j = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (j > 0) return `${j}j ${h}h ${m}m`;
  return `${h}h ${m}m`;
}

function PublicFooter() {
  return (
    <footer style={{
      padding: "32px clamp(16px, 4vw, 24px)",
      borderTop: `1px solid ${colors.border}`,
      background: colors.surface,
      textAlign: "center",
      color: colors.textMuted,
      fontSize: 13,
    }}>
      <p style={{ marginBottom: 8 }}>
        Propulsé par <strong style={{ color: colors.gold }}>Mon Ministère ✝</strong>
      </p>
      <p style={{ marginBottom: 16 }}>
        <a href="https://mon-ministere.com" style={{ color: colors.textMuted, textDecoration: "none" }}>
          mon-ministere.com
        </a>
      </p>
      <Link to="/inscription" style={{
        display: "inline-block",
        padding: "10px 18px",
        background: "transparent",
        border: `1px solid ${colors.gold}`,
        color: colors.gold,
        borderRadius: 999,
        fontWeight: 600,
        textDecoration: "none",
        fontSize: 13,
      }}>
        Créer la page de votre église →
      </Link>
    </footer>
  );
}

// ─── Helpers UI ───────────────────────────────────────────────────────

function Section({ id, title, accent, children }) {
  return (
    <section id={id} style={{ paddingTop: 8 }}>
      <h2 style={{
        fontFamily: fonts.heading,
        fontSize: 26,
        fontWeight: 700,
        marginBottom: 16,
        color: colors.text,
        borderBottom: `2px solid ${accent}`,
        paddingBottom: 8,
        display: "inline-block",
      }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 500 }}>{label}</span>
      {children}
    </label>
  );
}

function CenteredScreen({ children }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: colors.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    }}>
      {children}
    </div>
  );
}

const inputStyle = {
  background: colors.bg,
  color: colors.text,
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: 14,
  outline: "none",
  fontFamily: fonts.body,
  width: "100%",
};

const pillStyle = {
  padding: "8px 14px",
  border: `1px solid ${colors.border}`,
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 150ms",
};

function ctaButton(accent) {
  return {
    padding: "12px 22px",
    background: accent,
    color: "#050709",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
  };
}

function ctaButtonGhost(accent) {
  return {
    padding: "12px 22px",
    background: "transparent",
    color: accent,
    border: `1px solid ${accent}`,
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
  };
}

function primaryButton(accent, disabled) {
  return {
    padding: "12px 18px",
    background: accent,
    color: "#050709",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? "wait" : "pointer",
    opacity: disabled ? 0.7 : 1,
    fontFamily: fonts.body,
  };
}

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
